import { useEffect, useRef, useState } from "react";

import Input from "../../../controls/Input";

export default function ProductPriceRules({ product, onSave }) {
  const [rows, setRows] = useState(product?.price_rule_prices ?? []);
  const [saveStatuses, setSaveStatuses] = useState({});
  const savedValuesRef = useRef({});
  const saveQueuesRef = useRef({});

  const comparableValue = (row) =>
    JSON.stringify({
      availability: Boolean(row.availability),
      unit_price: row.unit_price === "" ? null : row.unit_price,
    });

  useEffect(() => {
    const nextRows = product?.price_rule_prices ?? [];
    setRows(nextRows);
    savedValuesRef.current = Object.fromEntries(
      nextRows.map((row) => [row.price_rule.value, comparableValue(row)]),
    );
  }, [product]);

  const updateRow = (ruleId, patch) =>
    setRows((current) =>
      current.map((row) =>
        row.price_rule.value === ruleId ? { ...row, ...patch } : row,
      ),
    );

  const save = (row) => {
    const ruleId = row.price_rule.value;
    const valueToSave = comparableValue(row);
    if (savedValuesRef.current[ruleId] === valueToSave) {
      return saveQueuesRef.current[ruleId] ?? Promise.resolve(true);
    }

    const operation = async () => {
      setSaveStatuses((current) => ({ ...current, [ruleId]: "saving" }));
      const saved = await onSave({
        product_id: product.id,
        price_rule_id: ruleId,
        availability: row.availability,
        unit_price: row.unit_price === "" ? null : row.unit_price,
      });
      if (!saved) {
        setSaveStatuses((current) => ({ ...current, [ruleId]: "error" }));
        return false;
      }

      setRows((current) =>
        current.map((currentRow) =>
          currentRow.price_rule.value === ruleId &&
          comparableValue(currentRow) === valueToSave
            ? { ...currentRow, ...saved }
            : currentRow,
        ),
      );
      savedValuesRef.current[ruleId] = comparableValue({ ...row, ...saved });
      setSaveStatuses((current) => ({ ...current, [ruleId]: "saved" }));
      return true;
    };

    const previous = saveQueuesRef.current[ruleId] ?? Promise.resolve();
    const task = previous.catch(() => false).then(operation);
    saveQueuesRef.current[ruleId] = task;
    task.finally(() => {
      if (saveQueuesRef.current[ruleId] === task) {
        delete saveQueuesRef.current[ruleId];
      }
    });
    return task;
  };

  if (!rows.length) {
    return (
      <div className="rounded-[18px] border bg-white p-6 text-sm text-slate-500">
        Create a price rule first to configure customer pricing.
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-[18px] border border-slate-200 bg-white">
      <div className="border-b border-slate-200 px-6 py-5">
        <h2 className="text-lg font-semibold text-slate-900">Price Rules</h2>
        <p className="mt-1 text-sm text-slate-500">
          Enable this product and set its selling price for each customer group.
        </p>
      </div>
      <div className="divide-y divide-slate-100">
        {rows.map((row) => (
          <div
            key={row.price_rule.value}
            className="grid gap-4 px-6 py-4 sm:grid-cols-[1fr_auto_12rem_4.5rem] sm:items-center"
          >
            <div>
              <p className="font-semibold text-slate-900">
                {row.price_rule.label}
              </p>
              <p className="text-xs text-slate-500">
                List-price fallback: $
                {Number(row.fallback_price ?? 0).toFixed(2)}
              </p>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={Boolean(row.availability)}
                onChange={(event) => {
                  const nextRow = {
                    ...row,
                    availability: event.target.checked,
                    unit_price:
                      event.target.checked &&
                      (row.unit_price == null || row.unit_price === "")
                        ? row.fallback_price
                        : row.unit_price,
                  };
                  updateRow(row.price_rule.value, nextRow);
                  save(nextRow);
                }}
              />
              Available
            </label>
            <Input
              type="number"
              name={`price-rule-${row.price_rule.value}`}
              min="0"
              step="0.0001"
              prefix="$"
              value={row.unit_price ?? ""}
              onChange={(event) =>
                updateRow(row.price_rule.value, {
                  unit_price: event.target.value,
                })
              }
              onBlur={() => save(row)}
              onKeyDown={(event) => {
                if (event.key === "Enter") event.currentTarget.blur();
              }}
              className="block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              aria-label={`${row.price_rule.label} unit price`}
            />
            <span
              className={`text-xs font-medium ${
                saveStatuses[row.price_rule.value] === "error"
                  ? "text-red-600"
                  : "text-slate-500"
              }`}
              role="status"
            >
              {saveStatuses[row.price_rule.value] === "saving"
                ? "Saving…"
                : saveStatuses[row.price_rule.value] === "saved"
                  ? "Saved"
                  : saveStatuses[row.price_rule.value] === "error"
                    ? "Try again"
                    : ""}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
